import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DEMO_PASSWORD } from "../constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Gem } from "lucide-react";
import { toast } from "sonner";

const DEMOS = [
  { role: "Employee", email: "employee@clarkacademy.com" },
  { role: "Manager", email: "manager@clarkacademy.com" },
  { role: "HR", email: "hr@clarkacademy.com" },
  { role: "Director", email: "director@clarkacademy.com" },
];

// Only expose demo credentials on local/preview environments, never in production.
function isDemoEnv() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".preview.emergentagent.com");
}
const SHOW_DEMO = isDemoEnv();

export default function Login() {
  const { doLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const res = await doLogin(email.trim(), password);
    if (res.error) { setErr(res.error); return; }
    toast.success(`Welcome back, ${res.user.firstName}!`);
    navigate("/app/dashboard");
  };

  const quick = (em) => { setEmail(em); setPassword(DEMO_PASSWORD); };

  return (
    <div className="min-h-screen bg-vault-bg flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-5 sm:px-8 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-vault-panel/70 hover:text-vault-panel transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
      <div className={`flex-1 grid gap-8 max-w-6xl w-full mx-auto px-5 sm:px-8 py-8 items-center ${SHOW_DEMO ? "lg:grid-cols-2" : ""}`}>
        {/* Form card */}
        <div className={`rounded-3xl bg-vault-panel p-8 sm:p-10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)] order-2 lg:order-1 w-full ${SHOW_DEMO ? "" : "max-w-md mx-auto"}`}>
          <div className="font-display text-3xl font-extrabold text-vault-cream">Member login</div>
          <p className="text-sm text-vault-subtle mt-2">Sign in to access your gems and dashboard.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-vault-subtle">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clarkacademy.com"
                className="bg-vault-panel2 border-white/10 text-vault-cream placeholder:text-vault-subtle/60 h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-vault-subtle">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-vault-panel2 border-white/10 text-vault-cream placeholder:text-vault-subtle/60 h-11" />
            </div>
            {err && <div className="text-sm text-red-400 bg-red-950/40 rounded-lg px-3 py-2">{err}</div>}
            <button type="submit" className="w-full rounded-2xl bg-vault-accent text-vault-panel font-bold py-3.5 hover:bg-vault-accentdark transition-colors">
              Sign in
            </button>
          </form>
          <p className="text-sm text-vault-subtle mt-6 text-center">
            New to The Vault? <Link to="/register" className="text-vault-accent font-semibold hover:underline">Create an account</Link>
          </p>
        </div>

        {/* Demo panel — development/preview only */}
        {SHOW_DEMO && (
        <div className="order-1 lg:order-2">
          <div className="flex items-center gap-2 mb-4">
            <Gem className="h-5 w-5 text-vault-panel" />
            <h2 className="font-display text-2xl font-extrabold text-vault-panel">Demo accounts</h2>
          </div>
          <p className="text-sm text-vault-panel/60 mb-5 max-w-sm">For development testing. Click any card to autofill, then press Sign in. Password: <span className="font-mono font-semibold text-vault-panel">{DEMO_PASSWORD}</span></p>
          <div className="grid sm:grid-cols-2 gap-3">
            {DEMOS.map((d) => (
              <button key={d.email} onClick={() => quick(d.email)}
                className="text-left rounded-2xl bg-vault-panel p-4 hover:-translate-y-0.5 transition-transform">
                <div className="text-xs font-bold uppercase tracking-widest text-vault-accent">{d.role}</div>
                <div className="text-sm text-vault-cream mt-1 truncate">{d.email}</div>
              </button>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
